import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import Navbar from './components/Navbar.jsx';
import MobileBottomNav from './components/MobileBottomNav.jsx';
import SiteFooter from './components/SiteFooter.jsx';
import HomePage from './pages/HomePage.jsx';

// Lazy-loaded pages for optimized mobile bundle splitting
const AboutPage = lazy(() => import('./pages/AboutPage.jsx'));
const AdmissionGuideFullPage = lazy(() => import('./pages/AdmissionGuideFullPage.jsx'));
const AdmissionGuidePage = lazy(() => import('./pages/AdmissionGuidePage.jsx'));
const ExamAdmissionGuideFullPage = lazy(() => import('./pages/ExamAdmissionGuideFullPage.jsx'));
const ExamAdmissionGuidePage = lazy(() => import('./pages/ExamAdmissionGuidePage.jsx'));
const EngineeringAdmissionGuidePage = lazy(() => import('./pages/EngineeringAdmissionGuidePage.jsx'));
const CollegeDetailsPage = lazy(() => import('./pages/CollegeDetailsPage.jsx'));
const EngineeringCollegeDirectoryPage = lazy(() => import('./pages/EngineeringCollegeDirectoryPage.jsx'));
const EngineeringCutoffTrendsPage = lazy(() => import('./pages/EngineeringCutoffTrendsPage.jsx'));
const CollegePredictorPage = lazy(() => import('./pages/CollegePredictorPage.jsx'));
const CollegesPage = lazy(() => import('./pages/CollegesPage.jsx'));
const CutoffTrendsPage = lazy(() => import('./pages/CutoffTrendsPage.jsx'));
const CutoffTrendsComparisonPage = lazy(() => import('./pages/CutoffTrendsComparisonPage.jsx'));
const ExamModulePlaceholderPage = lazy(() => import('./pages/ExamModulePlaceholderPage.jsx'));
const FaqPage = lazy(() => import('./pages/FaqPage.jsx'));
const PredictorPage = lazy(() => import('./pages/PredictorPage.jsx'));
const PredictorDataPage = lazy(() => import('./pages/PredictorDataPage.jsx'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage.jsx'));
const ResourceDetailsPage = lazy(() => import('./pages/ResourceDetailsPage.jsx'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage.jsx'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage.jsx'));
const OfficialGuideAdminPage = lazy(() => import('./pages/OfficialGuideAdminPage.jsx'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage.jsx'));
const GenericExamDataPage = lazy(() => import('./pages/GenericExamDataPage.jsx'));
const GenericExamCollegeDetailsPage = lazy(() => import('./pages/GenericExamCollegeDetailsPage.jsx'));
const GenericExamCollegeDirectoryPage = lazy(() => import('./pages/GenericExamCollegeDirectoryPage.jsx'));
const GenericExamPredictorPage = lazy(() => import('./pages/GenericExamPredictorPage.jsx'));
const UgmacCollegeDirectoryPage = lazy(() => import('./pages/UgmacCollegeDirectoryPage.jsx'));
const UgmacCollegeDetailsPage = lazy(() => import('./pages/UgmacCollegeDetailsPage.jsx'));
const UgmacDataPage = lazy(() => import('./pages/UgmacDataPage.jsx'));
const UgmacPredictorPage = lazy(() => import('./pages/UgmacPredictorPage.jsx'));
import { capturePostHogPageView } from './lib/posthog.js';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { usePWAInstall } from './hooks/usePWAInstall.js';
import InstallAppBanner from './components/InstallAppBanner.jsx';
import InstallAppModal from './components/InstallAppModal.jsx';

const getHashParts = () => {
  if (!window.location.hash.startsWith('#/')) {
    const rawPath = (window.location.pathname || '/').split('?')[0];
    return { routePath: rawPath || '/', anchor: '' };
  }

  const hashValue = window.location.hash.slice(1);
  const secondHashIndex = hashValue.indexOf('#', 1);

  const rawPath = secondHashIndex === -1 ? hashValue : hashValue.slice(0, secondHashIndex);
  const anchor = secondHashIndex === -1 ? '' : hashValue.slice(secondHashIndex + 1);

  return {
    routePath: (rawPath.split('?')[0] || '/'),
    anchor,
  };
};

const getAppLocation = () => getHashParts().routePath;

const clearAnchorScrollPadding = () => {
  document.body.style.paddingBottom = '';
};

const scrollToAnchorWithOffset = (anchor, behavior = 'smooth') => {
  const element = document.getElementById(anchor);
  if (!element) {
    return false;
  }

  const header = document.querySelector('header');
  const headerHeight = header?.getBoundingClientRect().height ?? 0;
  const extraOffset = window.innerWidth >= 1024 ? 22 : 18;
  const targetTop =
    element.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset;

  const doc = document.documentElement;
  const maxScroll = doc.scrollHeight - window.innerHeight;
  const paddingNeeded = Math.max(0, targetTop - maxScroll + 32);

  if (paddingNeeded > 0) {
    document.body.style.paddingBottom = `${paddingNeeded}px`;
  } else {
    clearAnchorScrollPadding();
  }

  window.scrollTo({
    top: Math.max(0, targetTop),
    behavior,
  });

  return true;
};

const resolveRoute = (pathname) => {
  if (pathname === '/') return { key: 'home' };
  if (pathname === '/predictor') return { key: 'predictor' };
  // Temporarily disabled: free registration and candidate portal (dashboard/auth)
  if (
    pathname === '/free-registration' ||
    pathname === '/login' ||
    pathname === '/signup' ||
    pathname === '/dashboard'
  ) {
    return { key: 'home' };
  }
  if (pathname === '/ugeac-cutoff-seat-matrix') return { key: 'predictor-data' };
  if (pathname === '/predictor-data') return { key: 'predictor-data' };
  if (pathname === '/college-predictor') return { key: 'college-predictor' };
  if (pathname === '/bcece-engineering-predictor') return { key: 'bcece-engineering-predictor' };
  if (pathname === '/bcece-engineering-cutoff-seat-matrix') return { key: 'bcece-engineering-data' };
  if (pathname === '/bcece-engineering-data') return { key: 'bcece-engineering-data' };
  if (pathname === '/bcece-engineering-colleges') return { key: 'bcece-engineering-colleges' };
  if (pathname.startsWith('/bcece-engineering-colleges/')) {
    return { key: 'bcece-engineering-college-detail', collegeSlug: pathname.replace('/bcece-engineering-colleges/', '') };
  }
  if (pathname === '/paramedical-predictor') return { key: 'paramedical-predictor' };
  if (pathname === '/paramedical-cutoff-seat-matrix') return { key: 'paramedical-data' };
  if (pathname === '/paramedical-data') return { key: 'paramedical-data' };
  if (pathname === '/paramedical-colleges') return { key: 'paramedical-colleges' };
  if (pathname.startsWith('/paramedical-colleges/')) {
    return { key: 'paramedical-college-detail', collegeSlug: pathname.replace('/paramedical-colleges/', '') };
  }
  if (pathname === '/polytechnic-predictor') return { key: 'polytechnic-predictor' };
  if (pathname === '/polytechnic-cutoff-seat-matrix') return { key: 'polytechnic-data' };
  if (pathname === '/polytechnic-data') return { key: 'polytechnic-data' };
  if (pathname === '/polytechnic-colleges') return { key: 'polytechnic-colleges' };
  if (pathname.startsWith('/polytechnic-colleges/')) {
    return { key: 'polytechnic-college-detail', collegeSlug: pathname.replace('/polytechnic-colleges/', '') };
  }
  if (pathname === '/ugmac-ayush-predictor') return { key: 'ugmac-ayush-predictor' };
  if (pathname === '/ugmac-ayush-cutoff-seat-matrix') return { key: 'ugmac-ayush-data' };
  if (pathname === '/ugmac-ayush-data') return { key: 'ugmac-ayush-data' };
  if (pathname === '/ugmac-ayush-colleges') return { key: 'ugmac-ayush-colleges' };
  if (pathname.startsWith('/ugmac-ayush-colleges/')) {
    return { key: 'ugmac-ayush-college-detail', collegeSlug: pathname.replace('/ugmac-ayush-colleges/', '') };
  }
  if (pathname === '/ugmac-cutoff-seat-matrix') return { key: 'ugmac-data' };
  if (pathname === '/ugmac-data') return { key: 'ugmac-data' };
  if (pathname === '/ugmac-predictor') return { key: 'ugmac-predictor' };
  if (pathname === '/ugmac-colleges') return { key: 'ugmac-colleges' };
  if (pathname.startsWith('/ugmac-colleges/')) {
    return { key: 'ugmac-college-detail', collegeSlug: pathname.replace('/ugmac-colleges/', '') };
  }
  if (pathname === '/engineering-cutoff-trends') return { key: 'cutoff-trends-comparison', initialModule: 'ugeac' };
  if (
    pathname === '/cutoff-trends-comparison' ||
    pathname === '/cutoff-comparison' ||
    pathname === '/all-cutoff-trends'
  ) {
    return { key: 'cutoff-trends-comparison', initialModule: 'ugeac' };
  }
  if (pathname === '/engineering-college-directory') return { key: 'engineering-college-directory' };
  if (pathname === '/colleges') return { key: 'colleges' };
  if (pathname === '/cutoff-trends') return { key: 'cutoff-trends' };
  if (pathname.startsWith('/colleges/')) {
    return { key: 'college-detail', collegeSlug: pathname.replace('/colleges/', '') };
  }
  if (pathname === '/admission-guide') return { key: 'admission-guide' };
  if (pathname === '/engineering-admission-guide') return { key: 'engineering-admission-guide' };
  if (pathname === '/admission-guide/full-guide') return { key: 'admission-guide-full' };
  if (pathname === '/admission-guide/full-guide-bcece') return { key: 'admission-guide-full-bcece' };
  if (pathname === '/modules/ugmac/admission-guide') return { key: 'ugmac-admission-guide' };
  if (pathname === '/modules/ugmac/admission-guide/full-guide')
    return { key: 'ugmac-admission-guide-full' };
  if (pathname === '/modules/ugmac-ayush/admission-guide')
    return { key: 'ugmac-ayush-admission-guide' };
  if (pathname === '/modules/ugmac-ayush/admission-guide/full-guide')
    return { key: 'ugmac-ayush-admission-guide-full' };
  if (pathname === '/modules/paramedical/admission-guide')
    return { key: 'paramedical-admission-guide' };
  if (pathname === '/modules/paramedical/admission-guide/full-guide')
    return { key: 'paramedical-admission-guide-full' };
  if (pathname === '/modules/polytechnic/admission-guide')
    return { key: 'polytechnic-admission-guide' };
  if (pathname === '/modules/polytechnic/admission-guide/full-guide')
    return { key: 'polytechnic-admission-guide-full' };
  if (pathname.startsWith('/modules/')) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length === 3) {
      if (parts[2] === 'cutoff-trends') {
        return {
          key: 'cutoff-trends-comparison',
          initialModule: parts[1],
        };
      }
      return {
        key: 'exam-module-placeholder',
        moduleKey: parts[1],
        pageType: parts[2],
      };
    }
  }
  if (pathname === '/about') return { key: 'about' };
  if (pathname === '/faq') return { key: 'faq' };
  if (pathname === '/resources') return { key: 'resources' };
  if (pathname === '/admin') return { key: 'admin-dashboard' };
  if (pathname === '/admin/guide-updates') return { key: 'admin-dashboard' };
  if (pathname === '/terms-and-conditions') return { key: 'terms-and-conditions' };
  if (pathname === '/privacy-policy') return { key: 'privacy-policy' };
  if (pathname.startsWith('/resources/')) {
    const resourcePath = pathname.replace('/resources/', '');
    const segments = resourcePath.split('/').filter(Boolean);

    if (segments.length >= 2) {
      return {
        key: 'resource-detail',
        resourceSlug: segments[0],
        resourceType: segments[1],
      };
    }
  }
  if (pathname.startsWith('/resources/')) {
    return { key: 'resource-detail', resourceSlug: pathname.replace('/resources/', '') };
  }
  if (pathname === '/contact') return { key: 'about' };
  return { key: 'home' };
};

const PageLoadingFallback = () => (
  <div className="page-shell min-h-[60vh]" aria-busy="true">
    <div className="space-y-4 motion-safe:animate-pulse sm:space-y-5" aria-hidden="true">
      <div className="h-9 w-36 rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="space-y-4 border-b border-slate-200 py-5 dark:border-slate-800">
        <div className="h-5 w-40 rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-8 w-11/12 max-w-xl rounded bg-slate-200/80 dark:bg-slate-800" />
        <div className="h-4 w-8/12 max-w-md rounded bg-slate-200/70 dark:bg-slate-800" />
        <div className="h-11 w-full max-w-xs rounded-lg bg-slate-200/70 dark:bg-slate-800" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-24 rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900" />
        ))}
      </div>
    </div>
  </div>
);

const ROUTE_SEO_META = {
  home: {
    title: 'CampusGuide by Ashwini | Bihar Admissions, Cutoffs & College Predictor',
    description: 'Accurate Bihar college predictor, official cutoff analysis, seat matrix, and counselling guidance for UGEAC (JEE Main), BCECE, NEET UGMAC, Polytechnic (DCECE PE), and Paramedical.',
  },
  predictor: {
    title: 'College Predictor Hub | CampusGuide Bihar',
    description: 'Predict your admission chances in Bihar engineering, medical, AYUSH, polytechnic, and paramedical colleges based on your rank, category, and gender quota.',
  },
  'college-predictor': {
    title: 'UGEAC Bihar Engineering College Predictor | JEE Main Ranks',
    description: 'Predict your B.Tech college and branch across all 38 government engineering colleges of Bihar based on JEE Main CRL / category rank.',
  },
  'bcece-engineering-predictor': {
    title: 'BCECE Engineering Predictor | PCM Cutoffs & Seat Matrix',
    description: 'Predict remaining seat allotments and lateral entry chances in Bihar engineering colleges via BCECE.',
  },
  'ugmac-predictor': {
    title: 'UGMAC Medical College Predictor | Bihar NEET-UG MBBS/BDS',
    description: 'Predict admission chances in Bihar government and private medical & dental colleges based on NEET-UG scores and category cutoffs.',
  },
  'ugmac-ayush-predictor': {
    title: 'UGMAC AYUSH Predictor | Bihar BAMS, BHMS & BUMS Cutoffs',
    description: 'Predict Ayurvedic, Homeopathic, and Unani medical colleges in Bihar based on NEET-UG ranks.',
  },
  'polytechnic-predictor': {
    title: 'DCECE Polytechnic Predictor | Bihar Diploma Engineering',
    description: 'Find your chances in 44+ Bihar government polytechnic institutes based on DCECE PE rank.',
  },
  'paramedical-predictor': {
    title: 'DCECE Paramedical Predictor | ANM, GNM, OT & Lab Tech',
    description: 'Predict Bihar paramedical college allotments for matric and inter level courses via DCECE PM/PMM.',
  },
  colleges: {
    title: 'Colleges Directory | Browse 300+ Bihar Institutes | CampusGuide',
    description: 'Explore engineering, medical, AYUSH, polytechnic, and paramedical colleges across all 38 districts of Bihar with cutoffs, branch matrices, fees, and hostel info.',
  },
  'college-detail': {
    title: 'College Details, Cutoffs & Branch Seat Matrix | CampusGuide',
    description: 'Detailed view of college campus info, category opening/closing cutoffs, seat matrices, and official website links.',
  },
  'cutoff-trends': {
    title: 'Seat Matrix & Cutoff Hub | CampusGuide Bihar',
    description: 'Official opening & closing ranks, category cutoffs, seat matrices, and multi-year admission trends for Bihar entrance exams.',
  },
  'cutoff-trends-comparison': {
    title: 'Multi-Year Cutoff Comparison (2025 vs 2026) | CampusGuide',
    description: 'Compare closing ranks across rounds, categories, and branches to spot rank inflation and shifting competition trends.',
  },
  'admission-guide': {
    title: 'Admission & Counselling Guide | CampusGuide Bihar',
    description: 'Step-by-step guidance on Bihar BCECEB counselling rounds, choice filling strategy, document verification, and allotment processes.',
  },
  'engineering-admission-guide': {
    title: 'UGEAC Engineering Admission Guide | Step-by-Step Counselling',
    description: 'Detailed guidance for Bihar engineering admissions, JEE Main eligibility, counselling registration, and physical verification.',
  },
  resources: {
    title: 'Student Academic Portal & Syllabus Resources | CampusGuide',
    description: 'Download Bihar Engineering University (BEU) syllabus, previous year question papers, credits guide, and curated study notes.',
  },
  about: {
    title: 'About CampusGuide | Bihar Education Intelligence',
    description: 'Learn about the mission, data accuracy standards, and story behind CampusGuide by Ashwini for Bihar students.',
  },
  faq: {
    title: 'Frequently Asked Questions | CampusGuide Bihar',
    description: 'Answers to common questions regarding Bihar counselling, UGEAC cutoffs, UGMAC NEET ranks, choice filling, and admission rules.',
  },
  'terms-and-conditions': {
    title: 'Terms & Conditions | CampusGuide',
    description: 'Read the terms and conditions for using the CampusGuide Bihar admissions and predictor platform.',
  },
  'privacy-policy': {
    title: 'Privacy Policy | CampusGuide',
    description: 'Privacy policy and data protection practices for CampusGuide users.',
  },
};

const App = () => {
  const [route, setRoute] = useState(() => resolveRoute(getAppLocation()));
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const lastTrackedPathRef = useRef('');
  const isDark = false;
  const { isInstalled, isIOS, showInstallModal, setShowInstallModal, triggerInstall } = usePWAInstall();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  useEffect(() => {
    const meta = ROUTE_SEO_META[route.key] || ROUTE_SEO_META.home;
    document.title = meta.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', meta.description);
    }
  }, [route]);

  useEffect(() => {
    const handleRouteChange = () => setRoute(resolveRoute(getAppLocation()));
    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('hashchange', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
    };
  }, []);

  useEffect(() => {
    const { anchor } = getHashParts();
    if (!anchor) {
      clearAnchorScrollPadding();
      return;
    }

    const timers = [
      window.setTimeout(() => {
        scrollToAnchorWithOffset(anchor, 'smooth');
      }, 80),
      window.setTimeout(() => {
        scrollToAnchorWithOffset(anchor, 'auto');
      }, 320),
      window.setTimeout(() => {
        scrollToAnchorWithOffset(anchor, 'auto');
      }, 640),
    ];

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [route]);

  useEffect(() => {
    const currentPath = getAppLocation();
    const trackingKey = `${currentPath}${window.location.hash}`;

    if (lastTrackedPathRef.current === trackingKey) {
      return;
    }

    lastTrackedPathRef.current = trackingKey;
    capturePostHogPageView(currentPath, {
      route_key: route.key,
    });
  }, [route]);

  const handleNavigate = (path, options = {}) => {
    setIsMobileMenuOpen(false);
    const { preserveScroll = false } = options;
    const [pathname, anchor = ''] = path.split('#');
    const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
    const hashPath = normalizedPath === '/' ? '#/' : `#${normalizedPath}`;
    const finalUrl = anchor ? `${hashPath}#${anchor}` : hashPath;

    window.history.pushState({}, '', finalUrl);
    setRoute(resolveRoute(pathname));

    if (anchor) {
      window.setTimeout(() => {
        scrollToAnchorWithOffset(anchor, 'smooth');
      }, 80);
      return;
    }

    clearAnchorScrollPadding();

    if (!preserveScroll) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const sharedProps = {
    currentPath: getAppLocation(),
    onNavigate: handleNavigate,
  };

  const renderPage = () => {
    switch (route.key) {
      case 'predictor':
        return <PredictorPage {...sharedProps} />;
      // Temporarily disabled
      // case 'free-registration':
      //   return <RegistrationPage {...sharedProps} />;
      // case 'candidate-login':
      //   return <CandidateAuthPage {...sharedProps} mode="login" />;
      // case 'candidate-signup':
      //   return <CandidateAuthPage {...sharedProps} mode="signup" />;
      // case 'candidate-dashboard':
      //   return <CandidateDashboardPage {...sharedProps} />;
      case 'college-predictor':
        return <CollegePredictorPage {...sharedProps} />;
      case 'predictor-data':
        return <PredictorDataPage {...sharedProps} />;
      case 'bcece-engineering-predictor':
        return <GenericExamPredictorPage {...sharedProps} moduleKey="bceceEngineering" />;
      case 'bcece-engineering-data':
        return <GenericExamDataPage {...sharedProps} moduleKey="bceceEngineering" />;
      case 'bcece-engineering-colleges':
        return <GenericExamCollegeDirectoryPage {...sharedProps} moduleKey="bceceEngineering" />;
      case 'bcece-engineering-college-detail':
        return <GenericExamCollegeDetailsPage {...sharedProps} moduleKey="bceceEngineering" collegeSlug={route.collegeSlug} />;
      case 'paramedical-predictor':
        return <GenericExamPredictorPage {...sharedProps} moduleKey="paramedical" />;
      case 'paramedical-data':
        return <GenericExamDataPage {...sharedProps} moduleKey="paramedical" />;
      case 'paramedical-colleges':
        return <GenericExamCollegeDirectoryPage {...sharedProps} moduleKey="paramedical" />;
      case 'paramedical-college-detail':
        return <GenericExamCollegeDetailsPage {...sharedProps} moduleKey="paramedical" collegeSlug={route.collegeSlug} />;
      case 'polytechnic-predictor':
        return <GenericExamPredictorPage {...sharedProps} moduleKey="polytechnic" />;
      case 'polytechnic-data':
        return <GenericExamDataPage {...sharedProps} moduleKey="polytechnic" />;
      case 'polytechnic-colleges':
        return <GenericExamCollegeDirectoryPage {...sharedProps} moduleKey="polytechnic" />;
      case 'polytechnic-college-detail':
        return <GenericExamCollegeDetailsPage {...sharedProps} moduleKey="polytechnic" collegeSlug={route.collegeSlug} />;
      case 'ugmac-ayush-predictor':
        return <GenericExamPredictorPage {...sharedProps} moduleKey="ugmacAyush" />;
      case 'ugmac-ayush-data':
        return <GenericExamDataPage {...sharedProps} moduleKey="ugmacAyush" />;
      case 'ugmac-ayush-colleges':
        return <GenericExamCollegeDirectoryPage {...sharedProps} moduleKey="ugmacAyush" />;
      case 'ugmac-ayush-college-detail':
        return <GenericExamCollegeDetailsPage {...sharedProps} moduleKey="ugmacAyush" collegeSlug={route.collegeSlug} />;
      case 'ugmac-data':
        return <UgmacDataPage {...sharedProps} />;
      case 'ugmac-predictor':
        return <UgmacPredictorPage {...sharedProps} />;
      case 'ugmac-colleges':
        return <UgmacCollegeDirectoryPage {...sharedProps} />;
      case 'ugmac-college-detail':
        return <UgmacCollegeDetailsPage {...sharedProps} collegeSlug={route.collegeSlug} />;
      case 'engineering-cutoff-trends':
        return <CutoffTrendsComparisonPage {...sharedProps} initialModule="ugeac" />;
      case 'engineering-college-directory':
        return <EngineeringCollegeDirectoryPage {...sharedProps} />;
      case 'exam-module-placeholder':
        return (
          <ExamModulePlaceholderPage
            {...sharedProps}
            moduleKey={route.moduleKey}
            pageType={route.pageType}
          />
        );
      case 'colleges':
        return <CollegesPage {...sharedProps} />;
      case 'cutoff-trends':
        return <CutoffTrendsPage {...sharedProps} />;
      case 'cutoff-trends-comparison':
        return <CutoffTrendsComparisonPage {...sharedProps} initialModule={route.initialModule || 'ugeac'} />;
      case 'college-detail':
        return <CollegeDetailsPage {...sharedProps} collegeSlug={route.collegeSlug} />;
      case 'admission-guide':
        return <AdmissionGuidePage {...sharedProps} />;
      case 'engineering-admission-guide':
        return <EngineeringAdmissionGuidePage {...sharedProps} />;
      case 'admission-guide-full':
        return <AdmissionGuideFullPage {...sharedProps} />;
      case 'admission-guide-full-bcece':
        return <AdmissionGuideFullPage {...sharedProps} process="bcece" />;
      case 'ugmac-admission-guide':
        return <ExamAdmissionGuidePage {...sharedProps} moduleKey="ugmac" />;
      case 'ugmac-admission-guide-full':
        return <ExamAdmissionGuideFullPage {...sharedProps} moduleKey="ugmac" />;
      case 'ugmac-ayush-admission-guide':
        return <ExamAdmissionGuidePage {...sharedProps} moduleKey="ugmacAyush" />;
      case 'ugmac-ayush-admission-guide-full':
        return <ExamAdmissionGuideFullPage {...sharedProps} moduleKey="ugmacAyush" />;
      case 'paramedical-admission-guide':
        return <ExamAdmissionGuidePage {...sharedProps} moduleKey="paramedical" />;
      case 'paramedical-admission-guide-full':
        return <ExamAdmissionGuideFullPage {...sharedProps} moduleKey="paramedical" />;
      case 'polytechnic-admission-guide':
        return <ExamAdmissionGuidePage {...sharedProps} moduleKey="polytechnic" />;
      case 'polytechnic-admission-guide-full':
        return <ExamAdmissionGuideFullPage {...sharedProps} moduleKey="polytechnic" />;
      case 'about':
        return <AboutPage {...sharedProps} />;
      case 'faq':
        return <FaqPage {...sharedProps} />;
      case 'resources':
        return <ResourcesPage {...sharedProps} />;
      case 'admin-dashboard':
        return <AdminDashboardPage {...sharedProps} />;
      case 'admin-guide-updates':
        return <OfficialGuideAdminPage {...sharedProps} />;
      case 'terms-and-conditions':
        return <TermsAndConditionsPage {...sharedProps} />;
      case 'privacy-policy':
        return <PrivacyPolicyPage {...sharedProps} />;
      case 'resource-detail':
        return (
          <ResourceDetailsPage
            {...sharedProps}
            resourceSlug={route.resourceSlug}
            resourceType={route.resourceType}
          />
        );
      default:
        return <HomePage {...sharedProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
      <Navbar
        currentPath={getAppLocation()}
        isDark={isDark}
        onNavigate={handleNavigate}
        onInstall={triggerInstall}
        isInstalled={isInstalled}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />
      <div>
        <Suspense fallback={<PageLoadingFallback />}>
          {renderPage()}
        </Suspense>
      </div>
      <SiteFooter onNavigate={handleNavigate} />
      <MobileBottomNav
        currentPath={getAppLocation()}
        onNavigate={handleNavigate}
        onOpenMenu={() => setIsMobileMenuOpen((prev) => !prev)}
        isMenuOpen={isMobileMenuOpen}
      />
      <InstallAppModal
        isOpen={showInstallModal}
        onClose={() => setShowInstallModal(false)}
        isIOS={isIOS}
      />
      <Analytics />
      <SpeedInsights />
    </div>
  );
};

export default App;
